import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LucideIcon, ListMusic, Plus } from 'lucide-react';
import { PlaylistsContext } from '../../../context/PlaylistsContext';
import { Track } from '../trackList/track';
// Reuses TrackMenu's portal/list/playlist-section styling (.trackMenu/
// .trackMenuItem/etc) - purely presentational classnames, no track-specific
// logic pulled in.
import '../trackMenu/TrackMenu.css';

export interface DetailMenuItem {
    label: string;
    icon: LucideIcon;
    onClick: () => void;
    danger?: boolean;
}

interface Props {
    /** Items rendered above the "Add to playlist" section (Play now, Play next, Add to queue, ...). */
    itemsBefore: DetailMenuItem[];
    /** The entity's own tracks, bulk-added to whichever playlist is picked. */
    tracks: Track[];
    /** Items rendered below the "Add to playlist" section (Download, info, Remove/Delete, ...). */
    itemsAfter: DetailMenuItem[];
    x: number;
    y: number;
    onClose: () => void;
}

// Generic "..." overflow menu for the album/artist/playlist detail hero
// action row - same outside-click/Escape-to-close portal pattern as
// TrackMenu, plus TrackMenu's own inline "Add to playlist" list/create-new
// section, generalized here to a bulk track list instead of one track.
export function DetailMoreMenu({ itemsBefore, tracks, itemsAfter, x, y, onClose }: Props): React.JSX.Element {
    const { playlists, createPlaylist, addTracksToPlaylist } = useContext(PlaylistsContext);
    const menuRef = useRef<HTMLDivElement>(null);
    const [pendingId, setPendingId] = useState<number | null>(null);
    const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        const handlePointerDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const trackIds = tracks.map((t) => t.getId());

    const handleAdd = async (playlistId: number) => {
        setPendingId(playlistId);
        const ok = await addTracksToPlaylist(playlistId, trackIds);
        setPendingId(null);
        if (ok) {
            setAddedIds((prev) => new Set(prev).add(playlistId));
            setTimeout(onClose, 500);
        }
    };

    const handleCreateAndAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;
        const playlist = await createPlaylist(newName.trim(), '');
        if (playlist) await handleAdd(playlist.id);
    };

    const renderItem = (item: DetailMenuItem, i: number) => (
        <button
            key={i}
            className={`trackMenuItem ${item.danger ? 'trackMenuItemDanger' : ''}`}
            onClick={() => {
                item.onClick();
                onClose();
            }}
        >
            <item.icon size={14} strokeWidth={2.75} />
            {item.label}
        </button>
    );

    return createPortal(
        <div className="trackMenu" style={{ left: x, top: y }} ref={menuRef}>
            {itemsBefore.map(renderItem)}

            <div className="trackMenuDivider" />

            <div className="trackMenuLabel">Add to playlist</div>
            <div className="trackMenuList">
                {playlists.length === 0 && !creating && <div className="trackMenuEmpty">No playlists yet</div>}
                {playlists.map((p) => (
                    <button
                        key={p.id}
                        className="trackMenuItem"
                        onClick={() => handleAdd(p.id)}
                        disabled={pendingId === p.id || trackIds.length === 0}
                    >
                        <ListMusic size={14} strokeWidth={2.75} />
                        <span className="trackMenuItemLabel">{p.name}</span>
                        {addedIds.has(p.id) && <span className="trackMenuAdded">Added</span>}
                    </button>
                ))}
            </div>

            {creating ? (
                <form className="trackMenuNewForm" onSubmit={handleCreateAndAdd}>
                    <input
                        className="trackMenuNewInput"
                        type="text"
                        placeholder="Playlist name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        maxLength={60}
                        autoFocus
                    />
                </form>
            ) : (
                <button className="trackMenuItem" onClick={() => setCreating(true)}>
                    <Plus size={14} strokeWidth={2.75} />
                    New playlist
                </button>
            )}

            <div className="trackMenuDivider" />

            {itemsAfter.map(renderItem)}
        </div>,
        document.body
    );
}
